'use client';

import { useQuery, useMutation } from '@tanstack/react-query';
import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';
import { ServiceFormsApi } from '@/lib/Apis/apis/service-forms-api';
import {
  ServiceForm,
  ServiceFormCategoryEnum,
  ServiceConsentTemplate,
  CreateSubmissionDto,
  ServiceSubmission,
} from '@/lib/Apis/models';

const api = () => new ServiceFormsApi(AXIOS_CONFIG);

// The generated `getSlots` is typed `AxiosResponse<void>`, so we describe the
// real payload here and cast at the call site.
export interface VaccineSlot {
  time: string;
  isBooked: boolean;
}
export interface SlotsResult {
  date: string;
  slots: VaccineSlot[];
}

// ── Plain async helpers (use anywhere, e.g. a home-screen banner) ────────────────

/**
 * Fetch the store's active shared-consent vaccines (the ones bookable together in
 * one visit through the shared questionnaire). Framework-agnostic — call it from
 * a banner, a server action, an effect, anywhere. The store key is injected
 * automatically by the shared axios config.
 */
export async function fetchVaccines(): Promise<ServiceForm[]> {
  const res = await api().getPublicForms(ServiceFormCategoryEnum.Vaccine, {
    params: { shared: 'true' },
  });
  // Defensive client-side filter in case the API predates the `shared` param.
  return (res.data ?? []).filter((v) => v.usesSharedForm !== false);
}

/**
 * Fetch the store's custom-form vaccines. Each carries its own questionnaire and
 * booking schedule, so they're booked one at a time through their own flow.
 */
export async function fetchCustomVaccines(): Promise<ServiceForm[]> {
  const res = await api().getPublicForms(ServiceFormCategoryEnum.Vaccine, {
    params: { shared: 'false' },
  });
  return (res.data ?? []).filter((v) => v.usesSharedForm === false);
}

/** Fetch a single service form (e.g. a custom vaccine) with its fields. */
export async function fetchVaccineForm(id: string): Promise<ServiceForm> {
  const res = await api().getForm(id);
  return res.data;
}

export async function fetchConsentTemplate(): Promise<ServiceConsentTemplate> {
  const res = await api().getConsentTemplate();
  return res.data;
}

export async function submitVaccineConsent(body: CreateSubmissionDto): Promise<ServiceSubmission> {
  const res = await api().createSubmission(body);
  return res.data;
}

// ── React Query hooks ───────────────────────────────────────────────────────────

const VACCINES_KEY = ['vaccines'] as const;
const CUSTOM_VACCINES_KEY = ['custom-vaccines'] as const;

export function useVaccines() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: VACCINES_KEY,
    queryFn: fetchVaccines,
    staleTime: 5 * 60 * 1000,
  });

  return {
    vaccines: data ?? [],
    isLoading,
    error: error as Error | null,
    refetch,
  };
}

/** Custom-form vaccines — each booked through its own questionnaire. */
export function useCustomVaccines() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: CUSTOM_VACCINES_KEY,
    queryFn: fetchCustomVaccines,
    staleTime: 5 * 60 * 1000,
  });

  return {
    vaccines: data ?? [],
    isLoading,
    error: error as Error | null,
    refetch,
  };
}

/** A single vaccine form (with fields + schedule), e.g. for the custom booking flow. */
export function useVaccineForm(id?: string) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['vaccine-form', id],
    queryFn: () => fetchVaccineForm(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });

  return {
    form: data ?? null,
    isLoading,
    error: error as Error | null,
  };
}

export function useConsentTemplate() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['vaccine-consent-template'],
    queryFn: fetchConsentTemplate,
    staleTime: 5 * 60 * 1000,
  });

  return {
    template: data ?? null,
    isLoading,
    error: error as Error | null,
  };
}

/**
 * Available booking slots for a vaccine on a date. Pass the shared visit duration so
 * slot length matches a vaccine visit regardless of how many vaccines are selected.
 */
export function useVaccineSlots(formId?: string, date?: string, duration?: number) {
  const { data, isLoading, error, refetch } = useQuery<SlotsResult>({
    queryKey: ['vaccine-slots', formId, date, duration],
    queryFn: () =>
      api()
        .getSlots(formId!, date!, String(duration ?? ''))
        .then((r) => r.data as unknown as SlotsResult),
    enabled: !!formId && !!date,
    staleTime: 30 * 1000,
  });

  return {
    slots: data?.slots ?? [],
    isLoading,
    error: error as Error | null,
    refetch,
  };
}

export function useSubmitVaccineConsent() {
  return useMutation({
    mutationFn: (body: CreateSubmissionDto) => submitVaccineConsent(body),
  });
}
